import React from 'react';
import { Alert, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import type { OrderDto } from '../../../dto/order';
import CurrencyAmount from '../../../components/CurrencyAmount';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import { getOrderMarginSummary } from '../orderMarginSummary';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';

interface Props {
  order: OrderDto;
}

/**
 * Planned margin of the order for a role that reads the WP5 plan without the
 * full finance view (sales rep on his own order, decision 2026-09-24). Only
 * the plan stored on the order: sale value, planned cost, result and percent.
 * No cost rows, settlement, margin history or warehouse valuation. Unknown cost
 * (`warehouseCostPending`) is shown as unknown, never as zero.
 */
export default function OrderPlannedMarginSummary({ order }: Props) {
  const { t, i18n } = useTranslation(['orders']);
  const summary = getOrderMarginSummary(order);
  const costUnknown = order.warehouseCostPending === true || order.margin == null;
  const money = (value: number) => formatCurrencyLocalized(value, 'PLN', i18n.language);
  const unknown = t('orders:orderDetail.plannedMarginUnknownValue');
  const percentLabel = costUnknown
    ? unknown
    : summary.percentUnavailableKey
      ? t(summary.percentUnavailableKey)
      : formatLocalizedPercent(summary.percent, i18n.language);

  return (
    <Card
      data-testid="order-planned-margin"
      title={renderSectionTitle(t('orders:orderDetail.plannedMarginTitle'))}
      style={SECTION_CARD_STYLE}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('orders:orderDetail.plannedMarginHint')} />
      {costUnknown && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('orders:orderDetail.plannedMarginCostUnknown')} />
      )}
      <div className="order-financial-summary">
        <div className="order-financial-summary__metric">
          <span className="order-financial-summary__label">{t('orders:orderDetail.revenue')}</span>
          <div className="order-financial-summary__value">
            <CurrencyAmount
              amount={order.exchangeRate ? summary.revenue / order.exchangeRate : summary.revenue}
              currency={order.currency}
              plnAmount={summary.revenue}
              rateDate={order.exchangeRateEffectiveDate || order.orderDate}
              rateSource={order.exchangeRateSource}
              rateTableNumber={order.exchangeRateTableNumber}
              strong
              primary="pln"
            />
          </div>
        </div>
        <div className="order-financial-summary__metric">
          <span className="order-financial-summary__label">{t('orders:orderDetail.plannedMarginCost')}</span>
          <strong className="order-financial-summary__value">{costUnknown ? unknown : money(summary.totalCost)}</strong>
        </div>
        <div className={`order-financial-summary__metric order-financial-summary__metric--result${costUnknown ? '' : ` order-financial-summary__metric--${summary.tone}`}`}>
          <span className="order-financial-summary__label">{t('orders:orderDetail.plannedMarginAmount')}</span>
          <strong className="order-financial-summary__value">{costUnknown ? unknown : money(summary.margin)}</strong>
        </div>
        <div className="order-financial-summary__metric">
          <span className="order-financial-summary__label">{t('orders:orderDetail.plannedMarginPercent')}</span>
          <strong className="order-financial-summary__value">{percentLabel}</strong>
        </div>
      </div>
    </Card>
  );
}
